/**
 * @file useMenuData.ts
 * @description Hook fetching live venue and menu data with realtime subscriptions for TableGreet Signage.
 */

import { useEffect, useState, useCallback } from 'react';
import { publicSupabase } from '../lib/supabase';
import type { VenueWithMenu } from '../types';

const PUBLIC_MENU_SELECT = `
  id,
  name,
  slug,
  description,
  logo_url,
  cover_url,
  operating_hours,
  phone,
  address,
  primary_color,
  border_radius,
  font_family,
  theme_settings,
  kitchen_load_status,
  qr_mode,
  tier,
  timezone,
  default_locale,
  enabled_locales,
  translations,
  country,
  currency,
  departments:departments(
    id,
    name,
    order,
    service_hour_id,
    service_hours:service_hours!service_hours_department_id_fkey(id,name,start_time,end_time,active_days,department_id)
  ),
  categories:categories(
    id,
    name,
    order,
    translations,
    availability_settings,
    department,
    department_id,
    service_hour_id,
    service_hours:service_hours(id,name,start_time,end_time,active_days),
    items:items(
      id,
      category_id,
      name,
      description,
      price,
      image_url,
      is_available,
      order,
      dietary_attributes,
      calories,
      ingredients,
      ingredient_data,
      avg_rating,
      rating_count,
      stock_status,
      prep_time_minutes,
      is_hidden,
      video_url,
      translations,
      availability_settings,
      department_id,
      service_hour_id,
      service_hours:service_hours(id,name,start_time,end_time,active_days)
    )
  ),
  campaigns:campaigns(id,type,content,start_time,end_time,is_active)
`;

export const useMenuData = (slug: string | undefined, domain?: string) => {
  const [venue, setVenue] = useState<VenueWithMenu | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchMenuData = useCallback(async (showLoading = false) => {
    if (!slug && !domain) return;
    setError(null);
    if (showLoading) setIsLoading(true);
    
    try {
      let query = publicSupabase
        .from('venues')
        .select(PUBLIC_MENU_SELECT)
        .eq('campaigns.is_active', true);

      if (domain) {
        query = query.eq('custom_domain', domain.trim().toLowerCase());
      } else if (slug) {
        query = query.eq('slug', slug.trim());
      }

      const { data: venueData, error: venueError } = await query
        .order('order', { foreignTable: 'categories', ascending: true })
        .order('order', { foreignTable: 'categories.items', ascending: true })
        .maybeSingle();

      if (venueError) throw venueError;

      if (!venueData) {
        setError('Venue not found. Please check the URL or configuration.');
        return;
      }

      const tier = venueData.tier || 'STARTER';
      const campaignsList = (venueData as any).campaigns || [];
      const announcementRow = campaignsList.find((c: any) => c.type === 'ANNOUNCEMENT' && c.is_active !== false);
      const featuredRow = campaignsList.find((c: any) => c.type === 'FEATURED_ITEMS' && c.is_active !== false);
      const promotionRow = campaignsList.find((c: any) => c.type === 'PROMOTION' && c.is_active !== false);
      const featuredItemIds = featuredRow?.content?.item_ids || [];

      let safeThemeSettings = venueData.theme_settings;
      if (typeof safeThemeSettings === 'string') {
        try {
          safeThemeSettings = JSON.parse(safeThemeSettings);
        } catch {
          safeThemeSettings = {};
        }
      }
      safeThemeSettings = typeof safeThemeSettings === 'object' && safeThemeSettings !== null ? safeThemeSettings : {};

      const processedVenueData = {
        ...venueData,
        tier,
        categories: (venueData.categories || []).map((category: any) => ({
          ...category,
          items: (category.items || []).filter((item: any) => !item.is_hidden).map((item: any) => ({
            ...item,
            is_featured: featuredItemIds.includes(item.id)
          }))
        })),
        theme_settings: {
          ...safeThemeSettings,
          campaigns: {
            announcement: announcementRow ? {
              active: announcementRow.is_active,
              text: announcementRow.content?.text || '',
              type: announcementRow.content?.style_type || 'info',
              action_url: announcementRow.content?.action_url || ''
            } : undefined,
            featured_items: featuredRow?.content?.item_ids || [],
            promotions: promotionRow?.content?.promotions || []
          }
        }
      };

      setVenue(processedVenueData as unknown as VenueWithMenu);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to load menu';
      setError(message);
      console.error('Error fetching menu for signage:', err);
    } finally {
      setIsLoading(false);
    }
  }, [slug, domain]);

  useEffect(() => {
    if (!slug && !domain) return;

    void fetchMenuData();

    const venueId = venue?.id;
    const subId = Math.random().toString(36).slice(2, 9);
    const channelName = venueId 
      ? `signage-menu-sync-${venueId}-${subId}` 
      : `signage-menu-sync-${slug || domain}-${subId}`;

    const channel = publicSupabase
      .channel(channelName)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'items' },
        () => {
          void fetchMenuData();
        }
      )
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'categories' },
        () => {
          void fetchMenuData();
        }
      )
      .subscribe();

    return () => {
      publicSupabase.removeChannel(channel);
    };
  }, [slug, domain, venue?.id, fetchMenuData]);

  return { venue, isLoading, error, refetch: () => fetchMenuData(true) };
};
